import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Layout from '../../components/Layout'
import Spinner from '../../components/Spinner'
import { getOrder, approveOrder, rejectOrder, activateOrder, setFirstPaymentDate, cancelOrder, deleteOrder, getOrderClientProfile, manualConfirmPayment, updateOrder } from '../../api/orders'
import { formatMoney, formatDate, orderStatusLabel, orderStatusColor, paymentStatusLabel, paymentStatusColor } from '../../utils/format'
import type { ClientCreditProfile } from '../../types'
import { useAuth } from '../../context/AuthContext'
import { useT } from '../../i18n'
import { useTelegram } from '../../hooks/useTelegram'

export default function AdminOrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { tg } = useTelegram()
  const lang = user?.language ?? 'ru'
  const tr = useT(lang)
  const qc = useQueryClient()

  const [customRate, setCustomRate] = useState('')
  const [note, setNote] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [showReject, setShowReject] = useState(false)
  const [pickedDate, setPickedDate] = useState('')
  const [showCancelSheet, setShowCancelSheet] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [showDeleteSheet, setShowDeleteSheet] = useState(false)
  const [deleteComment, setDeleteComment] = useState('')
  const [manualPayId, setManualPayId] = useState<number | null>(null)
  const [manualAmount, setManualAmount] = useState('')
  const [editMode, setEditMode] = useState(false)
  const [editProductName, setEditProductName] = useState('')
  const [editPrice, setEditPrice] = useState('')
  const [editDp, setEditDp] = useState('')
  const [editMonths, setEditMonths] = useState('')
  const [editRate, setEditRate] = useState('')
  const [editDiscount, setEditDiscount] = useState('')
  const [editFirstDate, setEditFirstDate] = useState('')
  const [editNote, setEditNote] = useState('')

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => getOrder(Number(id)),
    enabled: !!id,
  })

  const canApproveStatus = order?.status === 'pending' || order?.status === 'reviewing'
  const { data: creditProfile } = useQuery({
    queryKey: ['order-client-profile', id],
    queryFn: () => getOrderClientProfile(Number(id)),
    enabled: !!id && canApproveStatus,
  })

  useEffect(() => {
    tg?.BackButton.show()
    const cb = () => navigate(-1)
    tg?.BackButton.onClick(cb)
    return () => { tg?.BackButton.offClick(cb); tg?.BackButton.hide() }
  }, []) // eslint-disable-line

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['order', id] })
    qc.invalidateQueries({ queryKey: ['admin-orders'] })
  }

  const approveMut = useMutation({
    mutationFn: () => approveOrder(Number(id), {
      custom_rate: customRate ? Number(customRate) : undefined,
      note: note || undefined,
    }),
    onSuccess: () => { toast.success(tr('orderApproved')); tg?.HapticFeedback.notificationOccurred('success'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const rejectMut = useMutation({
    mutationFn: () => rejectOrder(Number(id), rejectReason),
    onSuccess: () => { toast.success(tr('orderRejected')); tg?.HapticFeedback.notificationOccurred('warning'); setShowReject(false); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const firstPaymentDateMut = useMutation({
    mutationFn: (dateStr: string) => setFirstPaymentDate(Number(id), dateStr),
    onSuccess: (updatedOrder) => {
      qc.setQueryData(['order', id], updatedOrder)
      toast.success(tr('firstPaymentDateSaved'))
      invalidate()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })


  const activateMut = useMutation({
    mutationFn: () => activateOrder(Number(id)),
    onSuccess: () => { toast.success(tr('orderActivated')); tg?.HapticFeedback.notificationOccurred('success'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const cancelMut = useMutation({
    mutationFn: (reason: string) => cancelOrder(Number(id), reason),
    onSuccess: () => { toast.success('Заявка отменена'); tg?.HapticFeedback.notificationOccurred('warning'); setShowCancelSheet(false); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const manualConfirmMut = useMutation({
    mutationFn: ({ paymentId, amount }: { paymentId: number; amount?: number }) =>
      manualConfirmPayment(Number(id), paymentId, amount),
    onSuccess: () => { toast.success('Платёж зачтён'); tg?.HapticFeedback.notificationOccurred('success'); setManualPayId(null); setManualAmount(''); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const deleteMut = useMutation({
    mutationFn: (comment: string) => deleteOrder(Number(id), comment),
    onSuccess: (res) => {
      toast.success(res.hard_delete ? 'Заявка удалена' : 'Заявка деактивирована')
      tg?.HapticFeedback.notificationOccurred('warning')
      setShowDeleteSheet(false)
      qc.invalidateQueries({ queryKey: ['admin-orders'] })
      navigate(-1)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const editMut = useMutation({
    mutationFn: () => {
      const p = Number(editPrice), dp = Number(editDp), m = Number(editMonths)
      const r = Number(editRate), disc = Number(editDiscount)
      return updateOrder(Number(id), {
        product_name: editProductName || undefined,
        product_price: editPrice ? p : undefined,
        down_payment: editDp ? dp : undefined,
        months: editMonths ? m : undefined,
        interest_rate: editRate ? r : undefined,
        discount_pct: editDiscount !== '' ? disc : undefined,
        first_payment_date: editFirstDate || undefined,
        manager_note: editNote !== '' ? editNote : undefined,
      })
    },
    onSuccess: () => {
      toast.success('Заявка обновлена')
      tg?.HapticFeedback.notificationOccurred('success')
      setEditMode(false)
      invalidate()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? tr('error')),
  })

  const openEdit = () => {
    if (!order) return
    const name = order.product ? order.product.name_ru : ''
    setEditProductName(name)
    setEditPrice(String(order.product_price))
    setEditDp(String(order.down_payment))
    setEditMonths(String(order.months))
    setEditRate(String(order.interest_rate))
    setEditDiscount(String(order.discount_pct))
    setEditFirstDate(order.first_payment_date ?? '')
    setEditNote(order.manager_note ?? '')
    setEditMode(true)
  }

  if (isLoading) return <Spinner className="h-screen" />
  if (!order) return null

  const productName = order.product ? (lang === 'uz' ? order.product.name_uz : order.product.name_ru) : '—'
  const isSuperadmin = user?.role === 'superadmin'
  const canApprove = canApproveStatus
  const canActivate = order.status === 'approved'
  const hasFirstPaymentDate = !!order.first_payment_date
  const canCancel = ['pending', 'reviewing', 'approved'].includes(order.status)
  const hasPaidPayments = order.payments?.some(p => p.status === 'paid') ?? false
  const canEdit = isSuperadmin && ['pending', 'reviewing', 'approved'].includes(order.status)

  // live recalc for edit mode (same formula as backend)
  const _p = Number(editPrice) || order.product_price
  const _dp = Number(editDp) || 0
  const _m = Number(editMonths) || order.months
  const _r = Number(editRate) || order.interest_rate
  const _disc = editDiscount !== '' ? Number(editDiscount) : order.discount_pct
  const _pad = _p * (1 - _disc / 100)
  const _loan = Math.max(0, _pad - _dp)
  const _monthly = _loan > 0 ? _loan * (1 + _r / 100) / _m : 0
  const _total = _monthly * _m + _dp

  return (
    <Layout hideNav>
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#A8A8AE', marginBottom: 16, fontSize: 13, background: 'none', border: 'none', padding: 0 }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        {tr('back')}
      </button>

      {/* Client info */}
      {order.user && (
        <div className="card mb-3">
          <p className="text-xs text-tg-hint">{tr('clientLabel')}</p>
          <p className="font-semibold text-tg-text">{order.user.full_name}</p>
          <p className="text-sm text-tg-hint">{order.user.phone}</p>
          <div className="flex gap-2 mt-1 text-xs">
            <span className={`px-2 py-0.5 rounded-full ${order.user.is_verified ? 'bg-tg-success-soft text-tg-success' : 'bg-tg-accent-soft text-tg-accent'}`}>
              {order.user.is_verified ? tr('verified') : tr('notVerified')}
            </span>
          </div>
        </div>
      )}

      {/* Order info */}
      <div className="card mb-3">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <p className="text-xs text-tg-hint">{order.registry_num}</p>
            <p className="font-medium text-tg-text text-sm">{productName}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${orderStatusColor(order.status)}`}>
              {orderStatusLabel(order.status, lang)}
            </span>
            {canEdit && !editMode && (
              <button onClick={openEdit} style={{ fontSize: 12, color: '#EAB308', background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.25)', borderRadius: 8, padding: '3px 10px' }}>
                Изменить
              </button>
            )}
          </div>
        </div>

        {editMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <EditField label="Товар / название" value={editProductName} onChange={setEditProductName} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <EditField label="Цена ($)" value={editPrice} onChange={setEditPrice} type="number" />
              <EditField label="Взнос ($)" value={editDp} onChange={setEditDp} type="number" />
              <EditField label="Срок (мес)" value={editMonths} onChange={setEditMonths} type="number" />
              <EditField label="Ставка (%)" value={editRate} onChange={setEditRate} type="number" />
              <EditField label="Скидка (%)" value={editDiscount} onChange={setEditDiscount} type="number" />
            </div>
            <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.04)', display: 'flex', gap: 16 }}>
              <div>
                <div style={{ fontSize: 9, color: '#8A8A96', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Ежемесячно</div>
                <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 14, color: '#F5F4F0', marginTop: 2 }}>{formatMoney(_monthly)}</div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: '#8A8A96', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Итого</div>
                <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 14, color: '#F5F4F0', marginTop: 2 }}>{formatMoney(_total)}</div>
              </div>
            </div>
            <EditField label="Дата первого платежа" value={editFirstDate} onChange={setEditFirstDate} type="date" />
            <div>
              <div style={{ fontSize: 10, color: '#8A8A96', marginBottom: 4 }}>Комментарий менеджера</div>
              <textarea
                className="input resize-none"
                rows={2}
                value={editNote}
                onChange={e => setEditNote(e.target.value)}
                placeholder="Необязательно"
              />
            </div>
            <div className="flex gap-3 mt-1">
              <button onClick={() => editMut.mutate()} disabled={editMut.isPending} className="btn-primary flex-1 disabled:opacity-40">
                Сохранить
              </button>
              <button onClick={() => setEditMode(false)} className="btn-secondary flex-1">
                Отмена
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-1.5 text-sm">
              <Row label={tr('productPrice')} value={formatMoney(order.product_price)} />
              {order.discount_pct > 0 && <Row label={tr('discount')} value={`${order.discount_pct}%`} />}
              <Row label={tr('priceAfterDiscount')} value={formatMoney(order.price_after_discount)} />
              <Row label={tr('downPayment')} value={formatMoney(order.down_payment)} />
              <Row label={tr('dueDate')} value={`${order.months} ${tr('monthsShort')}`} />
              <Row label={tr('rate')} value={`${order.interest_rate}%`} />
              <Row label={tr('monthly')} value={formatMoney(order.monthly_amount)} highlight />
              <Row label={tr('total')} value={formatMoney(order.total_amount)} />
              <Row label={tr('submittedAt')} value={formatDate(order.submitted_at)} />
            </div>
            {order.manager_note && (
              <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 12, background: 'rgba(234,179,8,0.09)', border: '1px solid rgba(234,179,8,0.25)' }}>
                <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#EAB308', marginBottom: 5 }}>
                  💬 Комментарий менеджера
                </div>
                <p style={{ fontSize: 13, color: '#F5F4F0', margin: 0 }}>{order.manager_note}</p>
              </div>
            )}
            {order.reject_reason && (
              <p className="mt-2 text-sm text-red-600">{tr('rejectReason')}: {order.reject_reason}</p>
            )}
          </>
        )}
      </div>

      {/* Client credit profile (shown to admin when order is pending/reviewing) */}
      {canApprove && creditProfile && (
        <CreditProfileCard profile={creditProfile} lang={lang} />
      )}

      {/* Approve form */}
      {canApprove && !showReject && (
        <div className="card mb-3">
          <h3 className="font-semibold text-tg-text mb-3">{tr('approveOrder')}</h3>
          <div className="mb-3">
            <label className="section-title">{tr('customRate')} ({order.interest_rate}%)</label>
            <input
              type="number"
              className="input mt-1"
              placeholder={String(order.interest_rate)}
              value={customRate}
              onChange={(e) => setCustomRate(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="section-title">{tr('note')}</label>
            <input className="input mt-1" placeholder={tr('optional')} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex gap-3">
            <button onClick={() => approveMut.mutate()} disabled={approveMut.isPending} className="btn-primary flex-1 disabled:opacity-40">
              {tr('approve')}
            </button>
            <button onClick={() => setShowReject(true)} className="btn-secondary flex-1">
              {tr('reject')}
            </button>
          </div>
        </div>
      )}

      {/* Reject form */}
      {showReject && (
        <div className="card mb-3">
          <h3 className="font-semibold text-tg-text mb-3">{tr('rejectOrder')}</h3>
          <textarea
            className="input resize-none"
            rows={3}
            placeholder={tr('specifyReason')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <div className="flex gap-3 mt-3">
            <button
              onClick={() => rejectMut.mutate()}
              disabled={!rejectReason.trim() || rejectMut.isPending}
              className="flex-1 py-3 rounded-xl font-semibold disabled:opacity-40"
              style={{ background: 'rgba(217,119,87,0.10)', color: '#D97757' }}
            >
              {tr('reject')}
            </button>
            <button onClick={() => setShowReject(false)} className="btn-secondary flex-1">{tr('cancel')}</button>
          </div>
        </div>
      )}

      {/* First payment date (shown when approved) */}
      {canActivate && (
        <div className="card mb-3">
          <h3 className="font-semibold text-tg-text mb-3">{tr('firstPaymentDate')}</h3>
          {hasFirstPaymentDate && (
            <p className="text-sm text-tg-hint mb-2">
              {tr('firstPaymentDate')}: <span className="text-tg-text font-medium">{order.first_payment_date}</span>
            </p>
          )}
          <MiniCalendar value={pickedDate} onChange={setPickedDate} />
          <button
            onClick={() => {
              if (!pickedDate) { toast.error('Выберите дату'); return }
              firstPaymentDateMut.mutate(pickedDate)
            }}
            disabled={!pickedDate || firstPaymentDateMut.isPending}
            className="btn-primary w-full disabled:opacity-40 mt-3"
          >
            {firstPaymentDateMut.isPending ? '...' : tr('save')}
          </button>
        </div>
      )}

      {/* Activate */}
      {canActivate && (
        <button
          onClick={() => activateMut.mutate()}
          disabled={!hasFirstPaymentDate || activateMut.isPending}
          className="btn-primary mb-3 disabled:opacity-40"
          title={!hasFirstPaymentDate ? tr('activateRequiresDate') : undefined}
        >
          {tr('activate')}
        </button>
      )}

      {/* Cancel reason display */}
      {order.status === 'cancelled' && order.cancel_reason && (
        <div className="card mb-3" style={{ borderColor: 'rgba(160,160,180,0.15)', background: 'rgba(160,160,180,0.04)' }}>
          <p className="text-xs text-tg-hint mb-1">
            {order.cancelled_by_role === 'client' ? 'Клиент отменил заявку' : 'Заявка отменена администратором'}
          </p>
          <p className="text-sm text-tg-text">{order.cancel_reason}</p>
        </div>
      )}

      {/* Payment schedule */}
      {order.payments && order.payments.length > 0 && (
        <div className="card mb-3">
          <h3 className="font-semibold text-tg-text mb-2">{tr('paymentSchedule')}</h3>
          {order.payments.map((p) => {
            const canManual = isSuperadmin && p.status !== 'paid'
            return (
              <div
                key={p.id}
                onClick={canManual ? () => setManualPayId(p.id) : undefined}
                className="flex items-center gap-3 py-2.5 border-b border-tg-hint/10 last:border-0"
                style={canManual ? { cursor: 'pointer', borderRadius: 8, marginInline: -8, paddingInline: 8 } : undefined}
              >
                <span className="w-6 h-6 rounded-full bg-tg-hint/10 flex items-center justify-center text-xs font-bold text-tg-hint">
                  {p.payment_num}
                </span>
                <div className="flex-1 text-sm">
                  <span className="text-tg-text">{formatMoney(p.amount)}</span>
                  <span className="text-tg-hint ml-2">{formatDate(p.due_date)}</span>
                  {p.status === 'partially_paid' && (
                    <p className="text-xs mt-0.5" style={{ color: '#EAB308' }}>
                      оплачено {formatMoney(p.paid_amount)} · остаток {formatMoney(p.amount - p.paid_amount)}
                    </p>
                  )}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${paymentStatusColor(p.status)}`}>
                  {paymentStatusLabel(p.status, lang)}
                </span>
              </div>
            )
          })}
        </div>
      )}

      {/* Admin actions: Cancel + Delete */}
      {canCancel && (
        <button
          onClick={() => { setCancelReason(''); setShowCancelSheet(true) }}
          className="w-full py-3 rounded-xl font-semibold mb-2"
          style={{ background: 'rgba(217,119,87,0.08)', color: '#D97757', border: '1px solid rgba(217,119,87,0.20)' }}
        >
          Отменить заявку
        </button>
      )}
      <button
        onClick={() => { setDeleteComment(''); setShowDeleteSheet(true) }}
        className="w-full py-3 rounded-xl font-semibold mb-6"
        style={{ background: 'rgba(220,38,38,0.08)', color: '#ef4444', border: '1px solid rgba(220,38,38,0.20)' }}
      >
        {hasPaidPayments ? 'Деактивировать заявку' : 'Удалить заявку'}
      </button>

      {/* Cancel sheet */}
      {showCancelSheet && (
        <div onClick={() => setShowCancelSheet(false)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, background: '#1C1C1F', borderRadius: '20px 20px 0 0', border: '1px solid rgba(255,255,255,0.08)', padding: '16px 20px 40px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 36, height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.12)' }} />
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F5F4F0', margin: '0 0 6px' }}>Отменить заявку</h3>
            <p style={{ fontSize: 12, color: '#8A8A96', marginBottom: 14 }}>Причина (необязательно)</p>
            <textarea
              className="input resize-none"
              rows={3}
              placeholder="Укажите причину..."
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
              style={{ marginBottom: 14 }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => cancelMut.mutate(cancelReason)}
                disabled={cancelMut.isPending}
                className="flex-1 py-3 rounded-xl font-semibold disabled:opacity-40"
                style={{ background: 'rgba(217,119,87,0.10)', color: '#D97757', border: '1px solid rgba(217,119,87,0.25)' }}
              >
                {cancelMut.isPending ? '...' : 'Отменить заявку'}
              </button>
              <button onClick={() => setShowCancelSheet(false)} className="btn-secondary flex-1">Назад</button>
            </div>
          </div>
        </div>
      )}

      {/* Manual confirm sheet (superadmin only) */}
      {manualPayId !== null && (() => {
        const p = order.payments?.find(x => x.id === manualPayId)
        if (!p) return null
        const remaining = p.amount - p.paid_amount
        const parsedAmt = parseFloat(manualAmount)
        const badAmt = manualAmount !== '' && !(parsedAmt > 0)  // 0, negative or junk must not fall through to "full payment"
        const submitAmt = manualAmount && parsedAmt > 0 ? parsedAmt : undefined
        const isOver = submitAmt !== undefined && submitAmt > remaining
        return (
          <div onClick={() => { setManualPayId(null); setManualAmount('') }} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
            <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, background: '#1C1C1F', borderRadius: '20px 20px 0 0', border: '1px solid rgba(255,255,255,0.08)', padding: '16px 20px 40px' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <div style={{ width: 36, height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.12)' }} />
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F5F4F0', margin: '0 0 4px' }}>Ручное зачисление</h3>
              <p style={{ fontSize: 12, color: '#8A8A96', margin: '0 0 16px' }}>
                Платёж №{p.payment_num} · к оплате {formatMoney(remaining)} · {formatDate(p.due_date)}
              </p>

              {/* Amount input */}
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: '#8A8A96', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Сумма ($) — пусто = полный платёж
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 10, padding: '10px 14px' }}>
                  <span style={{ color: '#C89B4C', fontFamily: 'monospace', fontSize: 16 }}>$</span>
                  <input
                    type="number" inputMode="decimal" min={0.01} step={0.01}
                    placeholder={remaining.toFixed(2)}
                    value={manualAmount}
                    onChange={e => setManualAmount(e.target.value)}
                    style={{ background: 'none', border: 'none', outline: 'none', flex: 1, color: '#F5F4F0', fontFamily: 'monospace', fontSize: 18 }}
                    autoFocus
                  />
                </div>
                {isOver && (
                  <p style={{ fontSize: 11, color: '#C89B4C', marginTop: 6 }}>
                    Переплата {formatMoney(parsedAmt - remaining)} перейдёт на следующие месяцы
                  </p>
                )}
              </div>

              <p style={{ fontSize: 11, color: '#3F3F45', marginBottom: 16 }}>Тип: наличные · Дата: сейчас</p>

              <div className="flex gap-3">
                <button
                  onClick={() => manualConfirmMut.mutate({ paymentId: p.id, amount: submitAmt })}
                  disabled={manualConfirmMut.isPending || badAmt}
                  className="flex-1 py-3 rounded-xl font-semibold disabled:opacity-40"
                  style={{ background: 'rgba(34,197,94,0.10)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.25)' }}
                >
                  {manualConfirmMut.isPending ? '...' : `Зачесть ${submitAmt ? formatMoney(submitAmt) : formatMoney(remaining)}`}
                </button>
                <button onClick={() => { setManualPayId(null); setManualAmount('') }} className="btn-secondary flex-1">Отмена</button>
              </div>
            </div>
          </div>
        )
      })()}

      {/* Delete / deactivate sheet */}
      {showDeleteSheet && (
        <div onClick={() => setShowDeleteSheet(false)} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, background: '#1C1C1F', borderRadius: '20px 20px 0 0', border: '1px solid rgba(255,255,255,0.08)', padding: '16px 20px 40px' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <div style={{ width: 36, height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.12)' }} />
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#F5F4F0', margin: '0 0 6px' }}>
              {hasPaidPayments ? 'Деактивировать заявку' : 'Удалить заявку'}
            </h3>
            {hasPaidPayments ? (
              <p style={{ fontSize: 12, color: '#8A8A96', marginBottom: 14 }}>
                У заявки есть оплаченные платежи — физическое удаление невозможно. Укажите причину деактивации (обязательно).
              </p>
            ) : (
              <p style={{ fontSize: 12, color: '#ef4444', marginBottom: 14 }}>
                Заявка будет <b>удалена из базы данных</b> без возможности восстановления.
              </p>
            )}
            <textarea
              className="input resize-none"
              rows={3}
              placeholder={hasPaidPayments ? 'Причина деактивации...' : 'Комментарий (необязательно)'}
              value={deleteComment}
              onChange={e => setDeleteComment(e.target.value)}
              style={{ marginBottom: 14 }}
            />
            <div className="flex gap-3">
              <button
                onClick={() => deleteMut.mutate(deleteComment)}
                disabled={deleteMut.isPending || (hasPaidPayments && !deleteComment.trim())}
                className="flex-1 py-3 rounded-xl font-semibold disabled:opacity-40"
                style={{ background: 'rgba(220,38,38,0.10)', color: '#ef4444', border: '1px solid rgba(220,38,38,0.25)' }}
              >
                {deleteMut.isPending ? '...' : hasPaidPayments ? 'Деактивировать' : 'Удалить'}
              </button>
              <button onClick={() => setShowDeleteSheet(false)} className="btn-secondary flex-1">Назад</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}

const TIER_COLOR: Record<string, { bg: string; text: string; border: string }> = {
  bronze: { bg: 'rgba(180,100,40,0.12)', text: '#C8752A', border: 'rgba(180,100,40,0.30)' },
  silver: { bg: 'rgba(160,160,180,0.12)', text: '#A0A0B4', border: 'rgba(160,160,180,0.30)' },
  gold:   { bg: 'rgba(200,155,76,0.12)',  text: '#C89B4C', border: 'rgba(200,155,76,0.30)' },
}

function CreditProfileCard({ profile, lang }: { profile: ClientCreditProfile; lang: string }) {
  const tc = TIER_COLOR[profile.tier] ?? TIER_COLOR.bronze
  const tierLabel = profile.tier === 'bronze' ? 'Bronze' : profile.tier === 'silver' ? 'Silver' : 'Gold'
  const usedPct = profile.credit_limit > 0
    ? Math.min(100, (profile.credit_used_with_order / profile.credit_limit) * 100)
    : 0
  const barColor = profile.is_over_limit ? '#ef4444' : usedPct > 75 ? '#f59e0b' : '#22c55e'

  return (
    <div className="card mb-3" style={{ border: profile.is_over_limit ? '1.5px solid rgba(239,68,68,0.35)' : undefined }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#8A8A96', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {lang === 'uz' ? 'Kredit profili' : 'Кредитный профиль'}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 99, background: tc.bg, color: tc.text, border: `1px solid ${tc.border}` }}>
          {tierLabel} · {profile.tier_score.toFixed(0)} pts
        </span>
      </div>

      {/* Over-limit alert */}
      {profile.is_over_limit && (
        <div style={{ padding: '8px 12px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', marginBottom: 12 }}>
          <p style={{ fontSize: 12, color: '#ef4444', margin: 0 }}>
            ⚠️ {lang === 'uz'
              ? `Limit $${profile.over_limit_amount.toFixed(2)} ga oshib ketgan`
              : `Превышен лимит на $${profile.over_limit_amount.toFixed(2)}`}
          </p>
        </div>
      )}

      {/* Credit limit bar */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
          <span style={{ fontSize: 11, color: '#8A8A96' }}>{lang === 'uz' ? 'Kredit limiti' : 'Кредитный лимит'}</span>
          <span style={{ fontSize: 11, color: '#F5F4F0', fontFamily: 'monospace' }}>
            ${profile.credit_used_with_order.toFixed(0)} / ${profile.credit_limit.toFixed(0)}
          </span>
        </div>
        <div style={{ height: 6, borderRadius: 99, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${Math.min(usedPct, 100)}%`, background: barColor, borderRadius: 99, transition: 'width 0.4s' }} />
        </div>
        {!profile.is_over_limit && (
          <p style={{ fontSize: 10, color: '#8A8A96', marginTop: 4, textAlign: 'right' }}>
            {lang === 'uz' ? 'Mavjud' : 'Свободно'}: ${Math.max(0, profile.credit_free).toFixed(0)}
          </p>
        )}
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', fontSize: 12 }}>
        <StatRow label={lang === 'uz' ? 'Jami arizalar' : 'Всего заявок'} value={String(profile.total_orders_count)} />
        <StatRow label={lang === 'uz' ? 'Yopilgan' : 'Закрытых'} value={String(profile.closed_orders_count)} />
        <StatRow label={lang === 'uz' ? "O'z vaqtida" : 'Вовремя'} value={String(profile.paid_on_time)} color="#22c55e" />
        <StatRow label={lang === 'uz' ? 'Kech to\'langan' : 'С опозданием'} value={String(profile.paid_late)} color={profile.paid_late > 0 ? '#f59e0b' : undefined} />
        {profile.open_overdue_count > 0 && (
          <StatRow label={lang === 'uz' ? 'Muddati o\'tgan' : 'Просроченных'} value={String(profile.open_overdue_count)} color="#ef4444" />
        )}
        {profile.has_active_overdue && (
          <StatRow label={lang === 'uz' ? 'Holat' : 'Статус'} value={lang === 'uz' ? '⚠️ Muddati o\'tgan' : '⚠️ Есть просрочка'} color="#ef4444" />
        )}
      </div>
    </div>
  )
}

function StatRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <p style={{ margin: 0, fontSize: 10, color: '#8A8A96' }}>{label}</p>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: color ?? '#F5F4F0', fontFamily: 'monospace' }}>{value}</p>
    </div>
  )
}

function EditField({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; type?: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#8A8A96', marginBottom: 4 }}>{label}</div>
      <input
        type={type}
        className="input"
        value={value}
        onChange={e => onChange(e.target.value)}
        step={type === 'number' ? 'any' : undefined}
      />
    </div>
  )
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-tg-hint">{label}</span>
      <span className={highlight ? 'font-bold text-tg-btn' : 'text-tg-text'}>{value}</span>
    </div>
  )
}

const MONTHS = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь']
const DAYS = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс']

function MiniCalendar({ value, onChange }: { value: string; onChange: (d: string) => void }) {
  const today = new Date()
  const [vy, setVy] = useState(today.getFullYear())
  const [vm, setVm] = useState(today.getMonth())

  const prevMonth = () => { if (vm === 0) { setVm(11); setVy(y => y - 1) } else setVm(m => m - 1) }
  const nextMonth = () => { if (vm === 11) { setVm(0); setVy(y => y + 1) } else setVm(m => m + 1) }

  const firstDow = (new Date(vy, vm, 1).getDay() + 6) % 7 // Mon=0
  const daysInMonth = new Date(vy, vm + 1, 0).getDate()
  const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)]

  const selDate = value ? new Date(value + 'T00:00:00') : null

  const pick = (day: number) => {
    onChange(`${vy}-${String(vm + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`)
  }

  const isSel = (day: number) => selDate && selDate.getFullYear() === vy && selDate.getMonth() === vm && selDate.getDate() === day
  const isTod = (day: number) => today.getFullYear() === vy && today.getMonth() === vm && today.getDate() === day

  return (
    <div style={{ background: '#0F0F12', borderRadius: 12, padding: '10px 8px', border: '1px solid rgba(255,255,255,0.07)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: '#A8A8AE', fontSize: 20, padding: '0 8px', cursor: 'pointer' }}>‹</button>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#F5F4F0' }}>{MONTHS[vm]} {vy}</span>
        <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: '#A8A8AE', fontSize: 20, padding: '0 8px', cursor: 'pointer' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 4 }}>
        {DAYS.map(d => <span key={d} style={{ textAlign: 'center', fontSize: 10, color: '#8A8A96', padding: '2px 0' }}>{d}</span>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((day, i) => day ? (
          <button
            key={i}
            onClick={() => pick(day)}
            style={{
              padding: '6px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13,
              background: isSel(day) ? '#F97316' : 'transparent',
              color: isSel(day) ? '#fff' : isTod(day) ? '#F97316' : '#F5F4F0',
              fontWeight: isSel(day) ? 700 : 400,
            }}
          >{day}</button>
        ) : <span key={i} />)}
      </div>
      {value && (
        <p style={{ textAlign: 'center', fontSize: 11, color: '#8A8A96', marginTop: 8 }}>
          Выбрано: <span style={{ color: '#F5F4F0' }}>{value}</span>
        </p>
      )}
    </div>
  )
}
